/**
 * The dashboard — "Pano": the workforce, and what to say to it next.
 *
 * A long-running agent finishes a turn — "committed and pushed", "done, here is
 * the evidence" — and goes quiet. This screen is where an operator reads what
 * each session last did and drives it forward. Nothing is typed without them;
 * the send goes through the driver, which types under the control lease and
 * never over a person (ADR-0017).
 *
 * One surface, read top to bottom in the order the questions are asked:
 *
 *   **ŞİMDİ — what needs a person.** The agents that are asking, each with the
 *   reply editor; the conductor's advice, each with the control that answers it;
 *   the chief's decisions; the open risks. Or one quiet line saying there is
 *   nothing, which is a claim and so waits for the overview to arrive.
 *
 *   **FİLO — what the workforce is doing.** One line per agent, taking the rest
 *   of the screen.
 *
 *   **Posture — whether it can keep working.** One line in the fleet screen's
 *   summary-strip idiom, *under* the board: "4/4 · 7/7 · 0 failed" nearly every
 *   time it is read is a footnote to the fleet, not a preamble to it.
 *
 * The mission and task machinery, and the conductor's batch planner, are on the
 * Tasks screen one click away.
 *
 * ---------------------------------------------------------------------------
 * Three shapes, and the measurement that ended each
 *
 * **A table** (eight columns). Right at eleven columns, wrong at eleven
 * sessions: account, model, host and age took 29% of a 1440 viewport printing
 * the same three strings on every row, the one column an operator reads was
 * squeezed to 493px and clipped at eighty characters, and at 2560 the slack went
 * to 977px of empty trailing cell beside a sentence that was still cut off.
 *
 * **A grid of cards.** It fixed the width and broke everything else. Sixteen
 * facts on each of eleven cards is 176 things on one screen; `.k-board__card`
 * and the panel under it were both `--k-surface`, a contrast of **1.00:1**, so
 * the cards had no ground; heights ran 157–207px *inside one row* and the last
 * row filled two tracks of five; and `IDLE` was toned `warn`, which put the same
 * amber on eight of the eleven. Colour on three quarters of a screen is not a
 * signal.
 *
 * **A list** — this one. Identical rows, aligned columns, one height, no holes,
 * five painted facts an agent (`SessionRow.tsx`), and what is asking for a
 * person lifted out of the list entirely rather than merely sorted to the top of
 * it. `IDLE` is `neutral` now, so the screen's colour budget is spent on the two
 * or three things that have somewhere to go.
 *
 * The vertical was fixed separately and still holds: the page spent 438px before
 * the first session — a pool strip and an advice panel both mostly saying
 * nothing is wrong — and spends 268 now, of which 178 is the shell's own chrome.
 * `e2e/dashboard.spec.ts` pins the geometry, the colour budget and the ground.
 */

import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import clsx from 'clsx'
import { ListChecks, Plus } from 'lucide-react'

import { api } from '@/api/client'
import { Icon } from '@/components/icon'
import {
  Button,
  EmptyState,
  Page,
  PageHeader,
  Panel,
  StatusDot,
  type Tone,
} from '@/components/primitives'
import { Notice, QueryState, TextField, useApiErrorMessage } from '@/components/controls'
import { useConfirm } from '@/components/confirm'
import { useLocale, useT } from '@/i18n/context'
import { formatRelative } from '@/i18n'
import { severityTone } from '@/lib/tone'
import { sessionHasEnded } from '@/lib/sessionActivity'
import { riskSentence } from '@/lib/riskSentence'
import {
  useAccountLabels,
  useHostLabels,
  useModelLabels,
  useProjectReadings,
} from '@/hooks/usePoolLabels'
import { useNow } from '@/hooks/useElapsed'
import { sessionLabel, useSessionActivity, useSessionsList, type SessionRow } from '@/terminal/wall'
import { AttentionCard, FleetRow, type SessionActs } from './SessionRow'
import { SupervisorPanel } from './SupervisorPanel'
import type { ID } from '@/types/domain'
import {
  Freshness,
  useOverview,
  type OverviewData,
  type OverviewRecommendation,
  type OverviewRisk,
} from './fleet-telemetry'
import { StartSessionDialog } from './StartSessionDialog'
import { SwitchAccountDialog } from './SwitchAccountDialog'
import {
  adviceActionKind,
  adviceSentence,
  adviceSessionId,
  matchesQuery,
  needsAttention,
  riskHref,
  splitBoard,
  unprovenSignal,
  summariseBoard,
  summarisePools,
} from './dashboardModel'
import './DashboardPage.css'


/* ------------------------------------------------------------ Pool posture */

/**
 * Can the workforce keep working, and what did it produce?
 *
 * One line, in the fleet screen's headline idiom — a mono figure, then the
 * lowercase words for what it counts — because that is what these numbers are
 * worth and because the console already has a shape for "the state of a pool,
 * above the list of it". A band of gauges here would be the fleet's own rejected
 * design a screen later.
 *
 * It deliberately does not restate the top bar. The bar carries the tightest
 * quota across every subscription, which is the figure that decides whether the
 * next hour is possible at all; these are the two it does not carry — how many
 * accounts the *router* would take work on, and how many machines answer — plus
 * the day's outcome, which is nowhere else in the console.
 *
 * The two pool figures are links, because a count an operator cannot act on is
 * decoration and both of those are things to go and fix (CLAUDE.md). The day's
 * pair is the rule's other half — leave it out or leave it plain: there is no
 * screen in this console that lists a day of endings, and a link to one that
 * does not contain them is worse than a plain fact.
 *
 * Nothing is drawn until the document has arrived. An unmeasured pool is not an
 * empty one, and "0/0 hesap" while a request is in flight is a sentence the
 * operator would act on.
 */
function PoolStrip({
  overview,
  updatedAt,
  unattended,
}: {
  overview: OverviewData
  updatedAt: number
  /** Live sessions started with the provider CLI's own permission prompts
   *  turned off, over live sessions. */
  unattended: { off: number; live: number }
}) {
  const t = useT()
  const pools = summarisePools(overview)
  const day = overview.sessions
  const accountTone: Tone =
    pools.accountsTotal === 0
      ? 'neutral'
      : pools.accountsReady === 0
        ? 'err'
        : pools.accountsReady < pools.accountsTotal
          ? 'warn'
          : 'ok'
  const hostTone: Tone =
    pools.hostsTotal === 0
      ? 'neutral'
      : pools.hostsOnline === 0
        ? 'err'
        : pools.hostsOnline < pools.hostsTotal
          ? 'warn'
          : 'ok'
  // Both counters are optional on the wire — only the overview runs the
  // time-ranged query behind them — so the group is drawn only when the numbers
  // are real. A zero invented for a figure nobody measured is the one thing this
  // console never does.
  const hasDay = typeof day?.completed24h === 'number' || typeof day?.failed24h === 'number'

  return (
    <section className="k-dash__posture" aria-label={t('dashboard.posture.title')}>
      <p className="k-dash__facts">
        <Link className="k-dash__fact" to="/accounts">
          <span className={clsx('k-mono', `k-dash__num--${accountTone}`)}>
            {pools.accountsReady}/{pools.accountsTotal}
          </span>{' '}
          {t('dashboard.posture.accounts')}
        </Link>
        <span className="k-dash__sep">·</span>
        <Link className="k-dash__fact" to="/hosts">
          <span className={clsx('k-mono', `k-dash__num--${hostTone}`)}>
            {pools.hostsOnline}/{pools.hostsTotal}
          </span>{' '}
          {t('dashboard.posture.hosts')}
          {pools.hostsDegraded > 0 && (
            <>
              {' · '}
              <span className="k-mono k-dash__num--warn">{pools.hostsDegraded}</span>{' '}
              {t('dashboard.posture.degraded')}
            </>
          )}
        </Link>
        {hasDay && (
          <>
            <span className="k-dash__sep">·</span>
            {/* Stated, not linked. The audit trail records what an *operator*
                did — logins, attachments, control handovers — so a link to it
                from "24 sessions ended well" would land somebody on a page that
                does not contain the thing they clicked. Nor is this a call on
                anyone's time: it is the measurement of whether the workforce is
                producing, and that is exactly what the fleet screen's own
                headline facts are. They are not links either. */}
            <span className="k-dash__day">
              {t('dashboard.posture.day')}{' '}
              <span className="k-mono k-dash__num--ok">{day?.completed24h ?? 0}</span>{' '}
              {t('dashboard.posture.completed')}
              {typeof day?.failed24h === 'number' && (
                <>
                  {' · '}
                  <span className={clsx('k-mono', day.failed24h > 0 && 'k-dash__num--err')}>
                    {day.failed24h}
                  </span>{' '}
                  {t('dashboard.posture.failed')}
                </>
              )}
            </span>
          </>
        )}
      {/* How much of the fleet is running unattended.
            
            Every live session on this deployment carries
            `launch.bypassApprovals: true`, and until now nothing said so anywhere.
            It is not ADR-0024's sign-off — that rule is about a *task* at L3 risk,
            and there are none — it is the provider CLI's own permission prompt,
            turned off wholesale at launch. What it changes for a reader of this
            screen is concrete: such a session never stops to ask before writing or
            running a command, so its silence can never mean "waiting for
            permission", and it will never appear in the WAITING band for one.
            
            Stated plainly, in the strip's own ink. Colouring a standing condition
            the operator chose, on every card, forever, is what CLAUDE.md calls
            decoration — and it is a ratio, so the day some sessions do ask, the
            figure says that by itself. */}
        {unattended.live > 0 && unattended.off > 0 && (
          <>
            <span className="k-dash__sep">·</span>
            <span className="k-dash__day" title={t('dashboard.posture.unattendedWhy')}>
              <span className="k-mono">
                {unattended.off}/{unattended.live}
              </span>{' '}
              {t('dashboard.posture.unattended')}
            </span>
          </>
        )}
      </p>
      <Freshness updatedAt={updatedAt} />
    </section>
  )
}

/* ----------------------------------------------------------------- Advice */

/** The controls a recommendation can be acted on with. Held as one object
 *  because every one of them already exists on this screen — the advice adds a
 *  second way to reach the board's own actions, never a second implementation
 *  of them. */
interface AdviceActs {
  open: (sessionId: ID) => void
  restart: (sessionId: ID) => void
  switchAccount: (sessionId: ID, target?: ID) => void
  start: () => void
  go: (path: string) => void
}

/**
 * The conductor's advice — what to do next, with the control to do it.
 *
 * This is the screen's title question ("şimdi ne yapmalı") and it was the one
 * thing on it with no renderer. The server has computed these since ADR-0015,
 * they travel on the same overview document the risks do, the sentences and the
 * action mapping have been in `dashboardModel` and under test the whole time —
 * and the dashboard dropped the array on the floor. An operator saw a session
 * list and had to work out for themselves that the account under one of them had
 * been exhausted for an hour.
 *
 * Each row is a sentence in the console's language, its age, and one control.
 * The control is chosen from the action's *kind* rather than its path, so
 * "switch this session's account" opens the dialog that re-plans and re-checks
 * the boundary instead of navigating somewhere and hoping — the recommendation
 * suggests, the endpoint decides (ADR-0015).
 */
function AdviceRow({ rec, acts }: { rec: OverviewRecommendation; acts: AdviceActs }) {
  const t = useT()
  const { locale } = useLocale()
  const sessionId = adviceSessionId(rec)
  const kind = adviceActionKind(rec)

  const control = (() => {
    switch (kind) {
      case 'open_session':
        return sessionId
          ? { label: t('live.open'), act: () => acts.open(sessionId) }
          : undefined
      case 'restart_session':
        return sessionId
          ? { label: t('live.restart'), act: () => acts.restart(sessionId) }
          : undefined
      case 'switch_account':
        return sessionId
          ? {
              label: t('live.switchAccount'),
              act: () => acts.switchAccount(sessionId, rec.action.accountProfileId),
            }
          : undefined
      case 'start_session':
        return { label: t('live.start'), act: () => acts.start() }
      case 'open_task':
        return { label: t('dashboard.advice.openTask'), act: () => acts.go(rec.action.path) }
      default:
        // A kind this build has never heard of still has somewhere to go: the
        // server sent a path, and a recommendation that renders without a
        // control is one an operator cannot act on.
        return rec.action.path
          ? { label: t('dashboard.advice.open'), act: () => acts.go(rec.action.path) }
          : undefined
    }
  })()

  return (
    <li className="k-advice__row">
      <StatusDot tone={severityTone(rec.severity)} label={rec.severity} />
      <span className="k-advice__text" title={rec.detail ?? rec.title}>
        {adviceSentence(rec, t)}
      </span>
      {/* Both trailing cells are always drawn, empty if they have nothing to
          say. They are grid tracks: a row that skipped its age put its control
          in the age's column, and one recommendation in four sat forty pixels
          left of the rest — a list of controls that does not line up reads as
          three lists. */}
      <span className="k-advice__since k-mono">
        {rec.since ? formatRelative(rec.since, locale) : ''}
      </span>
      <span className="k-advice__act">
        {control && (
          <Button size="sm" variant="ghost" onClick={control.act}>
            {control.label}
          </Button>
        )}
      </span>
    </li>
  )
}

/* ------------------------------------------------------------------ Risks */

/**
 * What is wrong with the pool, and where it is answered.
 *
 * The top bar's risk count links to this screen, so the list owes every line a
 * destination — the server already says what each risk is *about*, and
 * `riskHref` turns that into one. A dependency failure is the deliberate
 * exception: no screen here restarts MongoDB, so it is a statement rather than a
 * link to somewhere unhelpful.
 */
function RiskRow({ risk }: { risk: OverviewRisk }) {
  const t = useT()
  const href = riskHref(risk)
  const sentence = riskSentence(risk, t)
  return (
    <li className="k-risks__row">
      <StatusDot tone={severityTone(risk.severity)} label={risk.severity} />
      {href ? (
        <Link className="k-risks__text k-dash__risklink" to={href} title={risk.detail ?? risk.title}>
          {sentence}
        </Link>
      ) : (
        <span className="k-risks__text" title={risk.detail ?? risk.title}>
          {sentence}
        </span>
      )}
      <span className="k-mono k-risks__kind">{risk.kind}</span>
    </li>
  )
}

interface SwitchRequest {
  sessionId: ID
  initialTarget?: ID
}

export function DashboardPage() {
  const t = useT()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const confirm = useConfirm()
  const describe = useApiErrorMessage()

  const sessions = useSessionsList()
  const activityOf = useSessionActivity(sessions)
  // How long this browser has held the session payload. The server's `busyAgoMs`
  // and its siblings are measured when the response is built, so the two are
  // added rather than a server clock being compared with this laptop's — the
  // same arithmetic `useSessionActivity` does for the light, and it needs the
  // same clock so the duration on a card and the colour beside it are as of one
  // moment. One `useNow` for the page: React coalesces the two subscriptions.
  const now = useNow(1000)
  const heldMs = sessions.dataUpdatedAt ? now - sessions.dataUpdatedAt : 0
  const overview = useOverview()
  const accountLabels = useAccountLabels()
  const modelLabels = useModelLabels()
  const hostLabels = useHostLabels()
  const projectReadings = useProjectReadings()

  const [query, setQuery] = useState('')
  const [starting, setStarting] = useState(false)
  const [switching, setSwitching] = useState<SwitchRequest | null>(null)

  // The next-step disclosure, in two parts. `openNext` is the operator's own
  // choice and always wins; a row they have not touched follows the default,
  // which is open for the band that is asking for a person. `everOpen` keeps a
  // control mounted once it has been opened, so collapsing a row parks a typed
  // draft rather than throwing it away — the prefill rule's other half
  // (CLAUDE.md): no interaction may un-remember what somebody set.
  const [openNext, setOpenNext] = useState<Record<string, boolean>>({})
  const [everOpen, setEverOpen] = useState<Record<string, boolean>>({})
  // Which row was opened by a person. The board opens one by itself, and that
  // one must not take the keyboard: an operator who has just arrived is reading,
  // and a page that moves the caret into a textarea under them has taken the
  // scroll keys, the shortcuts and the browser's own find away without being
  // asked. A row they clicked is the opposite — they are there to type.
  const [asked, setAsked] = useState<Record<string, boolean>>({})

  const rows = useMemo(() => sessions.data?.items ?? [], [sessions.data])
  /* The search still matches the four facts the row stopped painting — the tmux
     handle, the account, the model and the host. They print the same three
     strings on eleven rows, which is why they are not columns any more; being
     able to type `SRV3` and see what is on that machine costs nothing and is the
     one time an operator wants them. */
  const visible = useMemo(
    () =>
      rows.filter((s) =>
        matchesQuery(
          [
            sessionLabel(s, s.id),
            s.tmuxSession,
            s.accountProfileId ? accountLabels.get(s.accountProfileId) : undefined,
            s.modelId ? modelLabels.get(s.modelId) : undefined,
            hostLabels.get(s.hostId),
          ],
          query,
        ),
      ),
    [rows, query, accountLabels, modelLabels, hostLabels],
  )
  const board = useMemo(() => splitBoard(visible, activityOf), [visible, activityOf])
  /* Counted over the rows the heading sits above, not over the whole fleet: a
     search that narrows the list must narrow the count with it, or the heading
     is describing sessions that are not on the screen. */
  const counts = useMemo(() => summariseBoard(board.fleet, activityOf), [board.fleet, activityOf])
  const byId = useMemo(() => new Map(rows.map((s) => [s.id, s])), [rows])

  // The one row the board opens by itself: the first that is asking for a
  // person, in the attention order the list is already sorted by. Opening every
  // such row was the old wall of editors in a new layout — eight finished turns
  // at once would have filled the screen with textareas again. One is a
  // recommendation an operator can act on or ignore; eight is a demand.
  const opensItself = useMemo(
    () => board.now.find((s) => !sessionHasEnded(s.state) && s.kind !== 'OBSERVED')?.id,
    [board.now],
  )

  const attention = useMemo(
    () => rows.filter((s) => needsAttention(activityOf(s))).length,
    [rows, activityOf],
  )
  /* "2 çalışıyor · 8 sessiz" — the counts the chips carried, on the heading that
     labels the thing they count. Composed rather than one interpolated key so a
     band with nothing in it contributes nothing: "0 çalışıyor" is a measured
     zero an operator cannot act on, which is the decoration CLAUDE.md refuses. */
  const fleetSummary = useMemo(() => {
    const parts: string[] = []
    if (counts.WORKING > 0) parts.push(t('dashboard.fleet.working', { count: counts.WORKING }))
    const quiet = counts.IDLE + counts.UNKNOWN
    if (quiet > 0) parts.push(t('dashboard.fleet.quiet', { count: quiet }))
    if (counts.ENDED > 0) parts.push(t('dashboard.fleet.ended', { count: counts.ENDED }))
    return parts.join(' · ')
  }, [counts, t])
  /* How much of the live fleet runs with the provider CLI's own permission
     prompts turned off. Counted over *live* sessions only: an ended one cannot
     be asked for permission either way, and including it would move the ratio
     for a reason that is not about autonomy. */
  const unattended = useMemo(() => {
    const live = rows.filter((s) => !sessionHasEnded(s.state))
    return { live: live.length, off: live.filter((s) => s.launch?.bypassApprovals).length }
  }, [rows])
  const risks = overview.data?.risks ?? []
  const advice = overview.data?.recommendations ?? []
  const hosts = overview.data?.hosts ?? []
  /* What the ŞİMDİ block holds. The chief's decisions are deliberately not in
     this count: `SupervisorPanel` renders itself or nothing depending on a query
     this component does not hold, so counting it would mean guessing — and it
     sits outside the block for the same reason, so a pending decision is never
     hidden by an otherwise calm moment. */
  /* The sessions Kimene is not actually measuring.
     
     This belongs in ŞİMDİ and nowhere else: the whole promise of the block is
     "here is what needs a person", and a broken waiting signal means that
     promise cannot be kept — quietly, and for as long as nobody notices. It is
     counted into nowCount so the calm line below can never say "nothing to do"
     on the strength of a measurement that was never taken. */
  const blind = useMemo(() => unprovenSignal(visible), [visible])
  const nowCount = board.now.length + advice.length + risks.length + (blind.length > 0 ? 1 : 0)
  const hasNow = nowCount > 0


  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['sessions'] })

  const stop = useMutation({
    mutationFn: (id: string) => api.del<void>(`/sessions/${id}`),
    onSuccess: refresh,
  })
  const restart = useMutation({
    mutationFn: (id: string) => api.post<SessionRow>(`/sessions/${id}/restart`, {}),
    onSuccess: (started) => {
      refresh()
      navigate(`/live/${started.id}`)
    },
  })

  const askRestart = async (s: SessionRow) => {
    const running = !sessionHasEnded(s.state)
    const ok = await confirm({
      title: t('live.restart'),
      message: t(running ? 'live.restartConfirmRunning' : 'live.restartConfirmEnded'),
      confirmLabel: t('live.restart'),
    })
    if (ok) restart.mutate(s.id)
  }
  const askStop = async (s: SessionRow) => {
    const ended = sessionHasEnded(s.state)
    const ok = await confirm({
      title: t(ended ? 'live.clear' : 'live.stop'),
      message: t(ended ? 'live.clearConfirm' : 'live.stopConfirm'),
      confirmLabel: t(ended ? 'live.clear' : 'live.stop'),
    })
    if (ok) stop.mutate(s.id)
  }

  // The advice acts through the board's own controls. A recommendation is a
  // second way to reach them, never a second implementation: `restart` runs the
  // same confirmation the kebab does, and `switchAccount` opens the same dialog
  // — with the conductor's suggested target as a *prefill*, which the operator
  // may overrule (CLAUDE.md: a prefill loses to a human).
  const acts: AdviceActs = {
    open: (id) => navigate(`/live/${id}`),
    restart: (id) => {
      const s = byId.get(id)
      if (s) void askRestart(s)
      else navigate(`/live/${id}`)
    },
    switchAccount: (id, target) => {
      if (byId.has(id)) setSwitching({ sessionId: id, initialTarget: target })
      else navigate(`/live/${id}`)
    },
    start: () => setStarting(true),
    go: (path) => navigate(path),
  }

  /* The card's own controls. The same acts the advice list reaches for — one
     implementation, two ways in — so a restart from a card runs the same
     confirmation as a restart from a recommendation. */
  const cardActs: SessionActs = {
    open: (id) => navigate(`/live/${id}`),
    restart: (s) => void askRestart(s),
    switchAccount: (id) => setSwitching({ sessionId: id }),
    stop: (s) => void askStop(s),
  }

  const toggleNext = (id: string, open: boolean) => {
    setEverOpen((prev) => ({ ...prev, [id]: true }))
    setOpenNext((prev) => ({ ...prev, [id]: !open }))
    setAsked((prev) => ({ ...prev, [id]: !open }))
  }

  /** Close one row's control, leaving the draft parked — Escape, and the send
   *  that has just finished with it. */
  const closeNext = (id: string) => setOpenNext((prev) => ({ ...prev, [id]: false }))

  const named = (id: ID | undefined, labels: Map<ID, string>) => (id ? (labels.get(id) ?? id) : undefined)

  const mutationError = stop.error ?? restart.error

  return (
    <Page
      header={
        <PageHeader
          title={t('dashboard.title')}
          /* A live count, which is what the subtitle slot holds on every other
             screen — the fleet says "7 çevrimiçi · 7 CLI eski", the audit says
             "Sayfa 1 · 50 kayıt". A slogan here was the one place the console
             advertised itself instead of reporting. */
          subtitle={
            rows.length === 0
              ? undefined
              : attention > 0
                ? t('dashboard.count.attention', { total: rows.length, attention })
                : t('dashboard.count.calm', { total: rows.length })
          }
          actions={
            <>
              <Button variant="primary" size="sm" onClick={() => setStarting(true)}>
                <Icon icon={Plus} size={15} />
                {t('live.start')}
              </Button>
              {/* A control, not a 12px text link. Header navigation on this
                  console is a button (the project screen's "Geri", the fleet's
                  "Sunucu ekle"); a bespoke link was the dashboard inventing a
                  seventh shape for something that already has one. */}
              <Link to="/tasks" className="k-dash__tolist">
                <Button size="sm" variant="ghost" tabIndex={-1}>
                  <Icon icon={ListChecks} size={14} />
                  {t('nav.missions')}
                </Button>
              </Link>
            </>
          }
        >
          {/* The one control that narrows the board, on the fixed header's own
              line so what an operator searched for stays on screen while the
              rows under it move.

              The activity chips were here — Tümü / Tur bitti / Çalışıyor /
              Boşta — and they went with the single ranked list they filtered.
              Splitting the board into ŞİMDİ and FİLO *is* that filter, applied
              always and for free; the chips were a second control saying the
              same thing, on the same line, competing for the same width. The
              counts they carried are on the section headings, where they label
              the thing they count. */}
          <div className="k-dash__filters">
            <TextField
              label={t('dashboard.search')}
              labelHidden
              type="search"
              value={query}
              onChange={setQuery}
              placeholder={t('dashboard.searchPlaceholder')}
            />
            {query !== '' && (
              <Button size="sm" variant="ghost" onClick={() => setQuery('')}>
                {t('common.clearFilters')}
              </Button>
            )}
          </div>
        </PageHeader>
      }
    >
      <div className="k-dash">
        {mutationError && <Notice tone="err">{describe(mutationError).title}</Notice>}

        {/* ---------------------------------------------------------- ŞİMDİ

            Everything that wants a person, in one block, on a raised ground so
            it is visibly a different object from the list under it.

            The previous board ranked these first among eleven identical cards,
            and ranking is not separating: the one agent that had finished its
            turn and was waiting to be told what to do next looked exactly like
            the ten that were getting on with things, so finding it meant reading
            all eleven. Lifting them out is what makes "what needs me" a glance
            instead of a scan — and it is why the row under this can afford to be
            calm.

            Four readings of the same moment, in the order they can be acted on:
            the agents that are asking, the conductor's advice, the chief's
            decisions, and the risks. The topbar's risk count links here
            expecting to find the last of them. */}
        {hasNow ? (
          <section className="k-dash__now" aria-labelledby="k-dash-now">
            <h2 className="k-label k-dash__nowhead" id="k-dash-now">
              {t('dashboard.now.title')}
              <span className="k-mono k-dash__lanen">{nowCount}</span>
            </h2>

            {board.now.length > 0 && (
              <ul className="k-board__asks">
                {board.now.map((s) => {
                  const drivable = !sessionHasEnded(s.state) && s.kind !== 'OBSERVED'
                  /* One editor open, not eight.
                   *
                   * The operator's own choice always wins; a row they have not
                   * touched follows the default, which is open for the *first*
                   * agent that is asking. Opening every one of them is the wall
                   * of textareas this screen already shipped once: eight
                   * finished turns at a time is a demand, and one is a
                   * recommendation that can be acted on or ignored. */
                  const open = drivable && (openNext[s.id] ?? s.id === opensItself)
                  return (
                    <AttentionCard
                      key={s.id}
                      session={s}
                      activity={activityOf(s)}
                      open={open}
                      mounted={everOpen[s.id] === true || open}
                      autoFocus={asked[s.id] === true}
                      onToggle={() => toggleNext(s.id, open)}
                      onDismiss={() => closeNext(s.id)}
                      onSent={refresh}
                      acts={cardActs}
                      heldMs={heldMs}
                      project={s.projectId ? projectReadings.get(s.projectId) : undefined}
                      accountLabel={named(s.accountProfileId, accountLabels)}
                      modelLabel={named(s.modelId, modelLabels)}
                      hostLabel={named(s.hostId, hostLabels)}
                    />
                  )
                })}
              </ul>
            )}

            {advice.length > 0 && (
              <div className="k-dash__lane">
                {/* Sub-headings, because the block holds three different kinds of
                    thing and an advice row and a risk row are the same shape: a
                    dot, a sentence and something at the end. One heading over all
                    of them would leave a reader to work out which sentences are
                    steps the conductor is proposing and which are facts about the
                    pool. h3 under the block's h2, so the outline is the nesting. */}
                <h3 className="k-label k-dash__lanehead" id="k-dash-advice">
                  {t('dashboard.advice.title')}
                </h3>
                {/* This list used to scroll inside its own lane, and was
                    focusable so that the rows past the sixth existed for a
                    keyboard as well as a mouse. It does not scroll any more: a
                    scroll container's scrollbar takes 10px out of its content
                    box, which put every recommendation's sentence, age and
                    control on rules 10px in from the fleet's. The page scrolls
                    instead — it is the console's one scrolling region. */}
                <ul className="k-advice" aria-labelledby="k-dash-advice">
                  {advice.map((rec, i) => (
                    <AdviceRow key={`${rec.kind}-${rec.targetId ?? ''}-${i}`} rec={rec} acts={acts} />
                  ))}
                </ul>
              </div>
            )}

            {/* Before the risks, because it qualifies every other reading on the
                screen: if the hook has never run, "nobody else is waiting" is
                not something Kimene knows. `unknown` rather than a warning
                colour — the fleet is not in a bad state, it is in an unmeasured
                one, and this vocabulary keeps those apart on purpose. */}
            {blind.length > 0 && (
              <div className="k-dash__lane">
                <h3 className="k-label k-dash__lanehead" id="k-dash-blind">
                  {t('dashboard.blind.title')}
                </h3>
                <ul className="k-risks" aria-labelledby="k-dash-blind">
                  <li className="k-risks__row">
                    <StatusDot tone="unknown" label={t('dashboard.blind.title')} />
                    <span className="k-risks__text" title={blind.map((s) => s.projectName ?? s.tmuxSession ?? s.id).join(', ')}>
                      {t('dashboard.blind.body', { count: String(blind.length) })}
                    </span>
                    <span className="k-mono k-risks__kind">{blind.length}</span>
                  </li>
                </ul>
              </div>
            )}

            {risks.length > 0 && (
              <div className="k-dash__lane">
                <h3 className="k-label k-dash__lanehead" id="k-dash-risks">
                  {t('dashboard.risks.title')}
                </h3>
                <ul className="k-risks" aria-labelledby="k-dash-risks">
                  {risks.map((r, i) => (
                    <RiskRow key={`${r.kind}-${r.targetId ?? ''}-${i}`} risk={r} />
                  ))}
                </ul>
              </div>
            )}
          </section>
        ) : (
          /* Nothing to do, said once and quietly, in the place the block would
             have been.

             Removing it outright was the wrong economy. Silence and a region
             that failed to render look identical on the screen whose promise is
             "what to do now", and the top bar's calm chip is not the same claim:
             it counts sessions waiting and open risks, and says nothing about
             whether the conductor has advice.

             Gated on the document having arrived, for the same reason the pool
             strip is: "nothing to do" is a claim, and until the overview is here
             there is no measurement behind it. */
          overview.data && (
            <p className="k-dash__calm">
              <StatusDot tone="ok" label={t('dashboard.advice.calm')} />
              {t('dashboard.advice.calm')}
            </p>
          )
        )}

        {/* The chief's decisions, when there are any to show. Outside the block
            above because it decides for itself whether it has anything to say,
            and a pending decision must not be hidden by an otherwise calm
            moment. */}
        <SupervisorPanel />

        {/* ------------------------------------------------------------- FİLO

            One line per agent. The counts the chips used to carry are on the
            heading, where they label the thing they count rather than offering a
            filter nobody applied. */}
        <Panel flush className="k-board">
          <QueryState
            pending={sessions.isPending}
            error={sessions.error}
            onRetry={() => void sessions.refetch()}
            empty={rows.length === 0}
            emptyTitle={t('dashboard.sessions.empty')}
            emptyHint={t('dashboard.sessions.emptyHint')}
            emptyAction={
              <Button variant="primary" size="sm" onClick={() => setStarting(true)}>
                <Icon icon={Plus} size={15} />
                {t('live.start')}
              </Button>
            }
          >
            {visible.length === 0 ? (
              <EmptyState
                title={t('dashboard.sessions.noMatch')}
                action={
                  <Button size="sm" onClick={() => setQuery('')}>
                    {t('dashboard.sessions.showAll')}
                  </Button>
                }
              />
            ) : (
              <>
                <h2 className="k-label k-board__head">
                  {t('dashboard.fleet.title')}
                  {fleetSummary && <span className="k-board__headn">{fleetSummary}</span>}
                </h2>
                {/* One grid for the whole list, and every row a subgrid of it, so
                    the columns line up down the screen rather than each row
                    resolving its own widths. A list, so a screen reader gets a
                    count and a position — the table this descends from had a
                    caption and column headers doing that job. */}
                <ul className="k-board__list" aria-label={t('dashboard.fleet.title')}>
                  {board.fleet.map((s) => {
                    const drivable = !sessionHasEnded(s.state) && s.kind !== 'OBSERVED'
                    const open = drivable && (openNext[s.id] ?? false)
                    return (
                      <FleetRow
                        key={s.id}
                        session={s}
                        activity={activityOf(s)}
                        open={open}
                        mounted={everOpen[s.id] === true}
                        autoFocus={asked[s.id] === true}
                        onToggle={() => toggleNext(s.id, open)}
                        onDismiss={() => closeNext(s.id)}
                        onSent={refresh}
                        acts={cardActs}
                        heldMs={heldMs}
                        project={s.projectId ? projectReadings.get(s.projectId) : undefined}
                        accountLabel={named(s.accountProfileId, accountLabels)}
                        modelLabel={named(s.modelId, modelLabels)}
                        hostLabel={named(s.hostId, hostLabels)}
                      />
                    )
                  })}
                </ul>
              </>
            )}
          </QueryState>
        </Panel>

        {/* Whether the workforce can keep working, and what it produced —
            under the fleet, not over it.

            It was the first thing on the screen and it says "everything is
            fine" nearly every time it is read: four green facts and, when the
            conductor had nothing to add, a fifth line saying so. That is 55px of
            the operator's first screenful spent on the absence of a problem, on
            a screen whose job is to show a dozen agents. Under the board it is
            still one glance away and it no longer costs the thing it is a
            footnote to.

            The second "everything is fine" is gone outright: the top bar already
            carries that claim, on every screen, and only once the overview has
            arrived. Two of them sixty pixels apart was the console telling an
            operator twice that it had nothing to tell them. */}
        {overview.data && (
          <PoolStrip
            overview={overview.data}
            updatedAt={overview.dataUpdatedAt}
            unattended={unattended}
          />
        )}
      </div>

      {switching &&
        (() => {
          const s = byId.get(switching.sessionId)
          if (!s) return null
          return (
            <SwitchAccountDialog
              sessionId={s.id}
              sessionName={sessionLabel(s, s.id)}
              provider={s.provider}
              host={hosts.find((h) => h.id === s.hostId)}
              hostName={hostLabels.get(s.hostId)}
              currentAccountId={s.accountProfileId}
              currentAccountLabel={s.accountProfileId ? accountLabels.get(s.accountProfileId) : undefined}
              initialTarget={switching.initialTarget}
              onClose={() => setSwitching(null)}
              onSwitched={(id) => {
                setSwitching(null)
                refresh()
                navigate(`/live/${id}`)
              }}
            />
          )
        })()}

      {starting && (
        <StartSessionDialog
          onClose={() => setStarting(false)}
          onStarted={(id) => {
            setStarting(false)
            refresh()
            navigate(`/live/${id}`)
          }}
        />
      )}
    </Page>
  )
}
